import type { TierSelection } from '@/lib/mapping';
import { bundleRefs as importedBundleRefs } from '@/lib/bundleRefs';

const config = Object.freeze({
  paymentMethods: {
    CREDITCARD: 'creditcard',
    SEPA: 'directdebit',
    PAYPAL: 'paypal',
    KLARNA: 'sofort',
    VOUCHER_PAYMENT: 'Gutscheinzahlung',
    NOT_SET: 'notset'
  },
  routes: {
    product: '/produkt',
    address: '/adresse',
    login: '/anmelden',
    register: '/registrieren',
    supporter: '/supporter',
    payment: '/bezahlen',
    confirmation: '/danke',
    approve: '/approve',
    error: '/fehler'
  },
  search: {
    activeProduct: '?code=activeProduct',
    challengeRequiredV1: '?creq=1',
    challengeRequiredV2: '?creq=2',
    emailInUse: '?code=emailInUse',
    notFound: '?code=404',
    paypalCancelled: '?code=paypalCancelled',
    sessionExpired: '?code=440'
  },
  subscriptionGroups: {
    pur: 'pur',
    supporter: 'supporter'
  },
  supporterPeriods: {
    single: 'single',
    monthly: 'monthly',
    yearly: 'yearly'
  },
  development: {
    supporterRefs: {
      '001PB879NLLGY4QMT0': 'single',
      '001PBMTARFFD442LG1': 'monthly',
      '001PB1L1M6J28YW231': 'yearly'
    },
    bundleRefs: importedBundleRefs.development
  },
  production: {
    supporterRefs: {
      '001PBKIP0MRZ3YPJ80': 'single',
      '001PBOSKDO7X77G611': 'monthly',
      '001PBB14NB7VMNQBE2': 'yearly'
    },
    bundleRefs: importedBundleRefs.production
  },
  bundleNames: {
    pur: 'derStandard.at PUR',
    purAndPrint: 'derStandard.at PUR + STANDARD Wochenende',
    purDaily: 'derStandard.at PUR Tagesabo',
    purForSubscribers: 'derStandard.at PUR-Kombi für Abonnenten',
    purAndPrintYearly: 'derStandard.at PUR-Kombi Jahresabo',
    purYearly: 'derStandard.at PUR Jahresabo',
    supporterSingle: 'Einmalige Unterstützung',
    supporterMonthly: 'Monatliche Unterstützung',
    supporterYearly: 'Jährliche Unterstützung',
    smartWithoutPur: 'derStandard Smart ohne PUR', // will be renamed to smart
    smart: 'derStandard Smart' // will get a new name
  },
  biPixel: {
    mediaTypes: {
      androidApp: 8,
      iphoneApp: 5,
      mobile: 2,
      web: 1
    }
  },
  analytics: {
    trackingID: import.meta.env.NODE_ENV === 'development' ? 'UA-000000000-0' : 'UA-126846403-7'
  },
  cookie: {
    consent: 'DSGVO_ZUSAGE_V1',
    domain: import.meta.env.VITE_APP_COOKIE_DOMAIN
  },
  voucher: {
    minLength: 8
  }
} as const);

const reusablePaymentMethods = Object.freeze([config.paymentMethods.CREDITCARD, config.paymentMethods.PAYPAL] as const);

/**
 * List of countries eligible for purchase
 *
 * key: ISO-3166-1 alpha 3 code (see https://www.wikiwand.com/en/ISO_3166-1#/Current_codes)
 * value: Country name in German
 */
const countryList = Object.freeze({
  '': 'Bitte wählen Sie ein Land aus',
  // Priorized countries
  AUT: 'Österreich',
  DEU: 'Deutschland',
  CHE: 'Schweiz',
  USA: 'USA',

  // Other countries, alpahabetically sorted by value
  BEL: 'Belgien',
  BIH: 'Bosnien und Herzegowina',
  BRA: 'Brasilien',
  BGR: 'Bulgarien',
  DNK: 'Dänemark',
  EST: 'Estland',
  FIN: 'Finnland',
  FRA: 'Frankreich',
  GRC: 'Griechenland',
  IRE: 'Irland',
  ITA: 'Italien',
  CAN: 'Kanada',
  HRV: 'Kroatien',
  LVA: 'Lettland',
  LTU: 'Litauen',
  LUX: 'Luxemburg',
  MLT: 'Malta',
  NLD: 'Niederlande',
  NOR: 'Norwegen',
  POL: 'Polen',
  PRT: 'Portugal',
  ROU: 'Rumänien',
  SWE: 'Schweden',
  SVK: 'Slowakei',
  SVN: 'Slowenien',
  THA: 'Thailand',
  TUR: 'Türkei',
  ESP: 'Spanien',
  CZE: 'Tschechien',
  UKR: 'Ukraine',
  HUN: 'Ungarn',
  GBR: 'Vereinigtes Königreich',
  CYP: 'Zypern'
} as const);

const salutationList = Object.freeze({
  '': 'Anrede',
  female: 'Frau',
  male: 'Herr'
} as const);

const titleList = Object.freeze({
  '': 'Bitte wählen',
  bachelor: 'Bachelor',
  bakkalareaut: 'Bakkalaurea/Bakkalaureus',
  diplom: 'Diplom-Ingenieur:in',
  magister: 'Magistra/Magister',
  master: 'Master',
  doktor: 'Doktor:in'
} as const);

const env = (import.meta.env.VITE_APP_ENVIRONMENT || 'development') as 'development' | 'production';
const supporterRefs = Object.freeze('supporterRefs' in config[env] ? config[env].supporterRefs : {});

const supporterTiers: readonly TierSelection[] = Object.freeze(['single', 'monthly', 'yearly']);

const isSupporterTier = (value: unknown): value is TierSelection => {
  return supporterTiers.includes(value as TierSelection);
};

const supporterPeriodAliases = Object.freeze({
  single: ['single', 'einmalig'],
  monthly: ['monthly', 'monatlich'],
  yearly: ['yearly', 'jaehrlich', 'jahrlich', 'jährlich']
} as const);

const supporterRoutes: Readonly<Record<string, string>> = Object.freeze({
  single: 'einmalige-unterstuetzung',
  monthly: 'monatliche-unterstuetzung',
  yearly: 'jaehrliche-unterstuetzung'
} as const);

const errorMessages = Object.freeze({
  invalidCreditCard: {
    response: 'The credit-card number passed is not valid',
    message: 'Die angegebene Kreditkartennummer ist nicht gültig.'
  },
  preAuthFailed: {
    response: 'The credit card is not correct. A pre authorisation failed',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Die Kreditkarte wurde abgelehnt.',
    redirect: true
  },
  invalidCreditCardDate: {
    response: 'Credit Card Expiry Date Invalid',
    message: 'Das angegebene Enddatum der Kreditkarte ist ungültig.'
  },
  noCancelDirectDebit: {
    response: 'Failure to cancel existing direct debit wallet',
    message: 'Ihr bestehender SEPA Auftrag konnte nicht geändert werden. Bitte kontaktieren Sie den Customer Support.'
  },
  sepaSessionExpired: {
    response: 'Session has expired.',
    redirect: true
  },
  forbidden: {
    response: 'Forbidden',
    message: 'Der Zugriff wurde verweigert. Bitte versuchen Sie es etwas später noch einmal.'
  },
  invalidEmailOrPassword: {
    response: 'Kombination E-Mail-Adresse und Passwort ist ungültig.',
    message: 'Die Kombination E-Mail-Adresse und Passwort ist ungültig.'
  },
  userExists: {
    response: 'User exists',
    message: `Die E-Mail-Adresse <strong>{{userEmail}}</strong> ist bereits registriert. <a href="${
      import.meta.env.VITE_APP_PASSWORD_FORGOTTEN_URL
    }">Passwort vergessen</a>?`
  },
  incorrectEmailOrPassword: {
    response: 'Incorrect username or password.',
    message: 'Ungültiger Benutzername oder Passwort'
  },
  invalidCvv: {
    response: 'CV2 must be 3 digits in length.',
    message: 'Die Prüfziffer (CVB/CVV) muss 3 Zeichen beinhalten.'
  },
  paymentNotAuthorized: {
    response: 'Payment could not be Authorized.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Die Zahlung wurde abgelehnt.',
    redirect: true
  },
  incomingttokenExpired: {
    response: 'The incoming token has expired.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Sitzung ist abgelaufen.',
    redirect: true
  },
  invalid3dsData: {
    response: 'Invalid data provided to proceed with 3D Secure Authentication.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Es liegen nicht genügend Daten zur Autorisierung mittels 3D-Secure-Verfahren vor.',
    redirect: true
  },
  endpointTimeout: {
    response: 'Endpoint request timed out',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Paypal-Sitzung ist abgelaufen.',
    redirect: true
  },
  noEwallet: {
    response: 'Account has no eWallet.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Die Paypal-Verknüpfung konnte nicht geladen werden.',
    redirect: true
  },
  undeterminedServicePrice: {
    response: "Service Price couldn't be determined",
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Der Fehler CP100 hat die Bestellung verhindert.',
    redirect: true
  },
  invalid3dsProcess: {
    response: 'Unable to validate the 3D Secure Process due to downstream partners.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Der Fehler CP350 hat die Bestellung verhindert.',
    redirect: true
  },
  existingSubscription: {
    response: 'Cannot add another subscription to contractReference:',
    redirect: true
  },
  existingService: {
    response: 'A subscription for this service already exists',
    redirect: true
  },
  existingContract: {
    response: 'contract already purchased',
    redirect: true
  },
  emailInUse: {
    response: 'The email is already in use.',
    redirect: true
  },
  invalid3dsToken: {
    response: '3D secure token invalid',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Der Fehler CP300 hat die Bestellung verhindert.',
    redirect: true
  },
  invalidParameters: {
    response: 'One Or More Invalid Parameters',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Der Fehler CP110 hat die Bestellung verhindert.',
    redirect: true
  },
  preconditionFailed: {
    response: 'Precondition Failed.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Der Fehler CP120 hat die Bestellung verhindert.',
    redirect: true
  },
  authorizationDenied: {
    response: 'Authorisation denied: the supplied session ID does not have the correct privilege for this request',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Sitzung ist abgelaufen.',
    redirect: true
  },

  // Generic Error Message
  default: {
    message: 'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen sie es etwas später noch einmal.',
    redirect: true
  }
} as const);

const errorPageMessages = Object.freeze({
  pageNotFound: {
    title: 'Es ist ein Fehler aufgetreten',
    body: `
            <p>Das angeforderte Produkt ist uns nicht bekannt.</p>
            <p>Alle aktuellen Angebote finden Sie unter <a href="https://abo.derstandard.at" title=""DerStandard.at ABO">abo.derstandard.at</a></p>
        `
  },
  sessionExpiredUnknownProduct: {
    title: 'Die Sitzung ist abgelaufen',
    body: `
            <p>Das angeforderte Produkt ist uns nicht bekannt.</p>
            <p>Alle aktuellen Angebote finden Sie unter <a href="https://abo.derstandard.at" title=""DerStandard.at ABO">abo.derstandard.at</a></p>
        `
  },
  paypalCancelled: {
    title: 'Die Zahlung wurde durch Paypal abgebrochen',
    body: `
            <p>Falls dieser Abbruch unerwünscht war, versuchen Sie es bitte zu einem späteren Zeitpunkt erneut.</p>
        `
  },
  activeProduct: {
    title: 'Sie haben bereits dieses Abo oder ein Abo aus dieser Produktgruppe erworben.',
    body: '<p>Falls hier ein Fehler vorliegt, wenden Sie sich bitte an den <a href="https://apps.derstandard.at/webshop/management/#!/subscriptions" title="Mein Abo">Customer Support</a>.</p>'
  },
  existingSubscription: {
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    body: '<p>Für dieses Konto besteht bereits ein aktives Abo.</p>'
  },
  emailInUse: {
    title: 'Bei der Anmeldung ist ein Fehler aufgetreten',
    body: '<p>Ihre Anmeldung kann mit diesem Konto aktuell nicht durchgeführt werden. Bitte wenden Sie sich an <a href="mailto:digitalabo@derstandard.at">digitalabo@derstandard.at</a>.<p>'
  },
  default: {
    title: 'Es ist ein Fehler aufgetreten',
    body: '<p>Alle aktuellen Angebote finden Sie unter <a href="https://abo.derstandard.at">abo.derstandard.at</a></p>'
  }
} as const);

const customOrderUTMParameters = ['utmTerm', 'utmContent', 'utmSource', 'utmMedium', 'utmCampaign'];

export {
  config,
  countryList,
  customOrderUTMParameters,
  errorMessages,
  errorPageMessages,
  isSupporterTier,
  reusablePaymentMethods,
  salutationList,
  supporterPeriodAliases,
  supporterRefs,
  supporterRoutes,
  supporterTiers,
  titleList
};
